/**
 * @deprecated — shim over `lib/transactions/categories.ts` and `lib/transactions/utils.ts`.
 *
 * Kept temporarily so the 21 existing consumers compile while M1 lands the
 * canonical source-of-truth. Will be deleted once consumers migrate to:
 *
 *   import { getCategory, getCategoryChipStyle, getCategoryChartColor } from '@/lib/transactions/categories';
 *   import { getAmountColor } from '@/lib/transactions/utils';
 *
 * See `docs/transactions-design.md`.
 */

import {
  CATEGORIES,
  FALLBACK_CATEGORY,
  getCategory,
  getCategoryChipStyle as getChipStyleById,
  getCategoryChartColor as getChartColorById,
} from './transactions/categories';

export { getAmountColor } from './transactions/utils';

export type CategoryColorIntent = 'expense' | 'income' | 'neutral';

// Legacy pt-BR keys → canonical ids, so existing call sites keep approximate
// visuals during the migration window. Anything not in this table falls back
// to the uncategorized slot.
const LEGACY_KEY_TO_ID: Record<string, string> = {
  // pt-BR keys → M6 prototype-canonical category ids
  essenciais: 'mercado',
  lazer: 'lazer',
  educacao: 'educacao',
  saude: 'saude',
  transporte: 'transporte',
  combustivel: 'combustivel',
  investimento: 'investimento',
  salario: 'renda',
  dividendos: 'investimento',
  reembolsos: 'renda',
  freelance: 'renda',
  retirada: 'transferencia',
  outros: 'uncategorized',
  alimentacao: 'mercado',
  mercado: 'mercado',
  restaurante: 'restaurante',
  delivery: 'delivery',
  moradia: 'moradia',
  farmacia: 'farmacia',
  viagem: 'compras',
  compras: 'compras',
  servicos: 'assinaturas',
  assinaturas: 'assinaturas',
  streaming: 'streaming',
  contas: 'assinaturas',
  poupanca: 'investimento',
  transferencia: 'transferencia',
  renda: 'renda',
};

export function normalizeCategoryKey(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim();
}

function resolveId(key: string): string {
  const norm = normalizeCategoryKey(key);
  if (LEGACY_KEY_TO_ID[norm]) return LEGACY_KEY_TO_ID[norm]!;
  // Already a canonical id?
  if (getCategory(norm) !== FALLBACK_CATEGORY) return norm;
  return FALLBACK_CATEGORY.id;
}

export function getCategoryChipStyle(
  key: string,
  _intent: CategoryColorIntent = 'neutral',
): { backgroundColor: string; color: string; accent: string } {
  return getChipStyleById(resolveId(key));
}

export function getCategoryChartColor(
  key: string,
  _intent: CategoryColorIntent = 'neutral',
): string {
  return getChartColorById(resolveId(key));
}

/** @deprecated — read `CATEGORIES.map(c => c.color)` instead. */
export const CATEGORY_COLORS: string[] = CATEGORIES.map((c) => c.color);

export function formatCategoryLabel(value: string, translated?: string): string {
  if (translated) return translated;
  if (!value) return FALLBACK_CATEGORY.label;
  const id = resolveId(value);
  if (id !== FALLBACK_CATEGORY.id) return getCategory(id).label;
  // Unknown legacy key — title-case the raw value.
  return normalizeCategoryKey(value)
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => `${w[0]?.toUpperCase() ?? ''}${w.slice(1)}`)
    .join(' ');
}
